'use client';

import { createContext, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

interface PropiedadesMarcoEscalado {
    /** Ancho y alto del escenario en el lienzo, en px: lo que se dibuja dentro nunca se recompone. */
    anchoDiseno: number;
    altoDiseno: number;
    /**
     * La escala del lienzo a 1440 (1 para los escenarios del héroe y la Biblioteca, 0,6 para el
     * mockup de Seccion_Mockups, 0,42 para el de la funcionalidad). Es la que da useEscalaMarco()
     * hasta la primera medición; el dibujo, en cambio, lo escala el CSS a cualquier ancho.
     */
    escalaInicial: number;
    children: ReactNode;
}

const ContextoEscala = createContext(1);

/**
 * La escala actual del marco. Las piezas con puntero la necesitan para pasar coordenadas de
 * pantalla a coordenadas del diseño, como hace el lienzo (`esc = rect.width / 1240`).
 */
export function useEscalaMarco(): number {
    return useContext(ContextoEscala);
}

/** El ancho medido de la caja, o null hasta la primera medición en el cliente. */
function useAnchoObservado(): [RefObject<HTMLDivElement | null>, number | null] {
    const ref = useRef<HTMLDivElement>(null);
    const [ancho, setAncho] = useState<number | null>(null);
    useEffect(() => {
        const nodo = ref.current;
        if (!nodo || typeof ResizeObserver === 'undefined') return;
        const observador = new ResizeObserver(([entrada]) => setAncho(entrada.contentRect.width));
        observador.observe(nodo);
        return () => observador.disconnect();
    }, []);
    return [ref, ancho];
}

/**
 * La escala en CSS puro: el ancho de la caja (100cqw) entre el ancho del diseño. `tan(atan2())`
 * divide dos longitudes y da un número sin unidad, que es lo que `scale()` admite. Así el HTML del
 * servidor ya trae la escala correcta a cualquier ancho, antes de hidratar y sin JavaScript.
 */
export function escalaCss(anchoDiseno: number): string {
    return `scale(tan(atan2(100cqw, ${anchoDiseno}px)))`;
}

/**
 * Escala un escenario de geometría fija al ancho de su contenedor (UI-SPEC §Layout frame).
 * Hasta la primera medición escala el CSS (la caja es un contenedor de consultas); después, la
 * cifra exacta que mide el ResizeObserver, que es también la que reciben los hijos por
 * useEscalaMarco(). La escala se deriva del ancho observado; nunca se copia de las props a un estado.
 */
export default function MarcoEscalado({ anchoDiseno, altoDiseno, escalaInicial, children }: PropiedadesMarcoEscalado) {
    const [ref, ancho] = useAnchoObservado();
    const escala = ancho === null ? escalaInicial : ancho / anchoDiseno;
    const transformacion = ancho === null ? escalaCss(anchoDiseno) : `scale(${escala})`;

    return (
        <div ref={ref} className="relative w-full overflow-hidden @container" style={{ aspectRatio: `${anchoDiseno} / ${altoDiseno}` }}>
            <div
                data-escenario
                className="absolute left-0 top-0"
                style={{ width: anchoDiseno, height: altoDiseno, transform: transformacion, transformOrigin: 'top left' }}
            >
                <ContextoEscala.Provider value={escala}>{children}</ContextoEscala.Provider>
            </div>
        </div>
    );
}
